/**
 * Acompanhamento ao vivo de uma execução.
 *
 * Existe para tornar observável uma campanha que leva horas. Um gráfico parado
 * não permite avaliar se a taxa de atualização é adequada, se a janela visível
 * é suficiente, nem se a tela continua legível enquanto o dado chega — por isso
 * o protótipo **avança de verdade** sobre a série sintética.
 */

import { useEffect, useMemo, useState } from "react";
import { Button } from "@cloudflare/kumo";
import { PauseIcon, PlayIcon } from "@phosphor-icons/react";
import { LatencyOverTimeChart, type SeriesPoint } from "../components/charts.js";
import { Notice } from "../components/Notice.js";
import { Page } from "../components/Page.js";
import { Panel } from "../components/Panel.js";
import { Stat } from "../components/Stat.js";
import { snapshot } from "../data/source.js";
import { TARGET_TOKENS_PER_SEC } from "../fixtures/scale.js";

/** Cenário observado ao vivo: o candidato de compra sob carga alvo. */
const LIVE_SCENARIO_ID = 3;

/** Passos por segundo com que a janela avança. */
const STEPS_PER_TICK = 1;

/** Intervalo entre avanços da janela, em milissegundos. */
const TICK_MS = 700;

/** Intervalo entre amostras da série, em milissegundos — espelha o gerador. */
const SAMPLE_INTERVAL_MS = 5_000;

/**
 * Passo em que a janela começa e recomeça.
 *
 * Seis instantes são o mínimo para a série já ter forma; começar do zero mostraria um
 * gráfico vazio justamente no primeiro olhar.
 */
const FIRST_STEP = 6;

/** Amostras de acelerador por instante: a bancada tem quatro aceleradores. */
const GPUS_PER_STEP = 4;

/** Indicador pulsante do estado da execução. */
function LiveDot({ playing }: { readonly playing: boolean }): React.ReactElement {
  return (
    <span className="relative flex size-3">
      {playing && <span className="absolute inline-flex size-full animate-ping rounded-full bg-kumo-success opacity-75" />}
      <span className={`relative inline-flex size-3 rounded-full ${playing ? "bg-kumo-success" : "bg-kumo-line"}`} />
    </span>
  );
}

/** Painel ao vivo, com séries que avançam ao longo do tempo. */
export function Live(): React.ReactElement {
  const [step, setStep] = useState(FIRST_STEP);
  const [playing, setPlaying] = useState(true);

  const gpuMetrics = useMemo(() => snapshot.gpuMetrics.filter((m) => m.scenarioId === LIVE_SCENARIO_ID), []);
  const engineMetrics = useMemo(() => snapshot.engineMetrics.filter((m) => m.scenarioId === LIVE_SCENARIO_ID), []);
  const totalSteps = engineMetrics.length;

  // Avanço da janela. Ao chegar ao fim, recomeça — o protótipo precisa poder
  // ficar aberto durante a avaliação sem exigir recarga.
  useEffect(() => {
    if (!playing) {
      return;
    }
    const timer = setInterval(() => {
      setStep((current) => (current + STEPS_PER_TICK >= totalSteps ? FIRST_STEP : current + STEPS_PER_TICK));
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [playing, totalSteps]);

  const visibleEngine = useMemo(() => engineMetrics.slice(0, step), [engineMetrics, step]);
  const visibleGpu = useMemo(() => gpuMetrics.filter((_, index) => index < step * GPUS_PER_STEP), [gpuMetrics, step]);

  /**
   * Latência por token ao longo do tempo, derivada da vazão do motor.
   *
   * O primeiro instante é descartado: sem um anterior não há intervalo de onde
   * derivar a taxa, e incluí-lo desenharia uma queda a zero que não aconteceu.
   */
  const latencySeries = useMemo<SeriesPoint[]>(
    () =>
      visibleEngine.slice(1).map((metric, index) => {
        const prior = visibleEngine[index];
        const delta = prior === undefined ? 0 : metric.tokensGenerated - prior.tokensGenerated;
        const perRequest = delta > 0 ? (SAMPLE_INTERVAL_MS * Math.max(1, metric.runningBatch)) / delta : 0;
        return { x: index + 1, y: Number(perRequest.toFixed(2)), series: "latência por token (p95)" };
      }),
    [visibleEngine],
  );

  /** Séries de telemetria, agregadas por instante. */
  const telemetrySeries = useMemo<SeriesPoint[]>(() => {
    const byStep = new Map<number, { util: number; power: number; count: number }>();
    visibleGpu.forEach((metric, index) => {
      const bucket = Math.floor(index / GPUS_PER_STEP);
      const acc = byStep.get(bucket) ?? { util: 0, power: 0, count: 0 };
      byStep.set(bucket, { util: acc.util + metric.utilPct, power: acc.power + metric.powerW, count: acc.count + 1 });
    });
    const points: SeriesPoint[] = [];
    for (const [bucket, acc] of byStep) {
      points.push({ x: bucket, y: Number((acc.util / acc.count).toFixed(1)), series: "utilização (%)" });
      points.push({ x: bucket, y: Number((acc.power / acc.count).toFixed(0)), series: "potência (W)" });
    }
    return points;
  }, [visibleGpu]);

  const last = visibleEngine[visibleEngine.length - 1];
  const lastGpu = visibleGpu[visibleGpu.length - 1];
  const throttling = visibleGpu.some((m) => m.throttleReason !== null);

  return (
    <Page>
      <Panel bodyClassName="flex flex-wrap items-center gap-4">
        <span className="flex items-center gap-2 text-sm font-medium text-kumo-strong">
          <LiveDot playing={playing} />
          {playing ? "em execução" : "pausado"}
        </span>
        <Button variant="secondary" icon={playing ? PauseIcon : PlayIcon} onClick={() => setPlaying((p) => !p)}>
          {playing ? "Pausar" : "Retomar"}
        </Button>
        <span className="text-sm text-kumo-subtle">
          cenário {LIVE_SCENARIO_ID} · passo {step} de {totalSteps} · atualização a cada {TICK_MS} ms
        </span>
      </Panel>

      {throttling && (
        <Notice
          tone="error"
          title="Throttle detectado durante a execução"
          description="Os relógios variaram; esta execução não será comparável com as demais."
        />
      )}

      <div className="grid grid-cols-2 gap-5 xl:grid-cols-4">
        <Panel>
          <Stat label="fila de espera" value={last?.queueSize ?? 0} suffix="req" />
        </Panel>
        <Panel>
          <Stat label="lote em execução" value={last?.runningBatch ?? 0} suffix="req" />
        </Panel>
        <Panel>
          <Stat label="acerto de prefixo" value={(last?.prefixHitRate ?? 0) * 100} precision={1} suffix="%" />
        </Panel>
        <Panel>
          <Stat label="aceitação especulativa" value={(last?.mtpAcceptRate ?? 0) * 100} precision={1} suffix="%" />
        </Panel>
      </div>

      <Panel
        title="Latência por token ao longo do tempo"
        description="A linha tracejada é o alvo. A área acima dela é goodput perdido."
      >
        <LatencyOverTimeChart
          data={latencySeries}
          targetValue={1_000 / TARGET_TOKENS_PER_SEC}
          targetLabel={`alvo — ${TARGET_TOKENS_PER_SEC} tok/s`}
          yTitle="ms por token"
        />
      </Panel>

      <Panel title="Telemetria de acelerador" bodyClassName="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <Stat label="utilização" value={lastGpu?.utilPct ?? 0} precision={1} suffix="%" size="compact" />
          <Stat label="potência" value={lastGpu?.powerW ?? 0} suffix="W" size="compact" />
          <Stat
            label="relógio"
            value={lastGpu?.smClockMhz ?? 0}
            suffix="MHz"
            size="compact"
            tone={throttling ? "danger" : "default"}
          />
        </div>
        <LatencyOverTimeChart
          data={telemetrySeries}
          targetValue={100}
          targetLabel="saturação"
          yTitle="utilização (%) e potência (W)"
          height={240}
        />
      </Panel>
    </Page>
  );
}
